import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Anchor, ArrowLeft, AlertTriangle, Loader2, Trash2, Link2, Unlink, Check, CreditCard, XCircle, Smartphone } from "lucide-react";
import { getAppleIdentityPayload } from "@/components/AppleSignInButton";
import CancellationModal from "@/components/CancellationModal";
import { InstallButton } from "@/components/InstallPrompt";
import { usePwaInstall } from "@/lib/pwa";
import { isNativeApp, billingUIEnabled, canShowPlans, isIOSNative, openExternal } from "@/lib/platform";
import { goUpgrade } from "@/lib/plan";
import { openPaywall, APPLE_MANAGE_SUBSCRIPTIONS_URL } from "@/lib/iap";

/**
 * Account settings — for now, only contains the Apple-required
 * "Delete Account" flow. Future: change name/email, change password, etc.
 *
 * Apple guideline 5.1.1(v): apps that support account creation must let
 * users delete their account from within the app.
 */
export default function AccountSettings() {
    const { user, setUser, logout, refreshUser } = useAuth();
    const nav = useNavigate();
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const [linkBusy, setLinkBusy] = useState(false);
    const [linkErr, setLinkErr] = useState("");
    const [linkOk, setLinkOk] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [portalBusy, setPortalBusy] = useState(false);
    const [billingMsg, setBillingMsg] = useState("");
    // Owner-only "Admiral's Office" entry — role comes from /auth/me, web only.
    const isAdmin = user?.role === "admin" && !isNativeApp();
    const [billingErr, setBillingErr] = useState("");

    if (!user) {
        nav("/login", { replace: true });
        return null;
    }

    const expected = (user.email || "").trim().toLowerCase();
    const matches = confirm.trim().toLowerCase() === expected;

    const doDelete = async () => {
        setErr("");
        setBusy(true);
        try {
            await api.delete("/users/me", { data: { confirmation: confirm } });
            // Log out and bounce to landing with a friendly note.
            await logout();
            nav("/", { replace: true, state: { deleted: true } });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't delete account. Please try again.");
            setBusy(false);
        }
    };

    const linkApple = async () => {
        setLinkErr("");
        setLinkOk("");
        setLinkBusy(true);
        try {
            const payload = await getAppleIdentityPayload();
            const { data } = await api.post("/auth/apple/link", payload);
            setUser(data);
            setLinkOk("Apple ID linked. You can now sign in with either method.");
        } catch (e) {
            const code = e?.error || e?.code;
            if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
                setLinkBusy(false);
                return;
            }
            setLinkErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't link Apple ID.");
        } finally {
            setLinkBusy(false);
        }
    };

    const unlinkApple = async () => {
        setLinkErr("");
        setLinkOk("");
        setLinkBusy(true);
        try {
            const { data } = await api.post("/auth/apple/unlink");
            setUser(data);
            setLinkOk("Apple ID unlinked. You can still sign in with your email/password.");
        } catch (e) {
            setLinkErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't unlink Apple ID.");
        } finally {
            setLinkBusy(false);
        }
    };

    const openStripePortal = async () => {
        setBillingErr("");
        setPortalBusy(true);
        try {
            const { data } = await api.post("/billing/portal", { return_url: window.location.href });
            window.location.href = data.url;
        } catch (e) {
            setBillingErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal.");
            setPortalBusy(false);
        }
    };

    const onStayed = (kind) => {
        const map = {
            pause:    "Your subscription is paused. We'll keep your data safe and pick up right where you left off.",
            discount: "50% off applied to your next three billing cycles. No code needed.",
            gift:     "Your next billing cycle is on the house — enjoy!",
        };
        setBillingMsg(map[kind] || "Thanks for sticking around.");
        // Refresh user so the UI picks up any new subscription_paused_until / status
        refreshUser?.();
    };

    const onCanceled = () => {
        setBillingMsg("Your subscription is set to cancel at the end of the current period.");
        refreshUser?.();
    };

    const isSubscriber = user.subscription_status === "active" || user.subscription_status === "trialing";
    const isLifetime   = user.subscription_status === "lifetime";
    const appleBilled  = user.billing_provider === "apple";

    return (
        <div data-testid="account-settings-page" style={{ minHeight: "100vh", background: "var(--bm-bg-0)", paddingBottom: 80 }}>
            {/* Top bar */}
            <div className="bm-safe-top" style={{ borderBottom: "1px solid var(--bm-border-soft)", background: "var(--bm-bg-1)" }}>
                <div style={{ maxWidth: 920, margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16 }}>
                    <button
                        data-testid="account-back-btn"
                        type="button"
                        className="bm-btn bm-btn-ghost"
                        onClick={() => nav("/app")}
                        style={{ padding: "6px 10px" }}
                    >
                        <ArrowLeft size={16} /> Back
                    </button>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Anchor size={20} color="var(--bm-gold)" />
                        <h1 className="bm-display" style={{ margin: 0, fontSize: 18 }}>Account</h1>
                    </div>
                    {isAdmin && (
                        <button
                            data-testid="account-admin-btn"
                            type="button"
                            className="bm-btn bm-btn-ghost"
                            onClick={() => nav("/admin")}
                            style={{ padding: "6px 12px", marginLeft: "auto", color: "var(--bm-gold)", borderColor: "var(--bm-gold-dim)" }}
                        >
                            Admiral's Office
                        </button>
                    )}
                </div>
            </div>

            <div style={{ maxWidth: 920, margin: "0 auto", padding: "32px 24px" }}>
                {/* Account info card */}
                <section className="bm-card" style={{ padding: 26, marginBottom: 24 }}>
                    <h2 className="bm-serif" style={{ fontSize: 24, margin: "0 0 14px", fontWeight: 500 }}>
                        Signed in as
                    </h2>
                    <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "8px 18px", fontSize: 14, color: "var(--bm-text-muted)" }}>
                        <div>Name</div>
                        <div data-testid="account-name" style={{ color: "var(--bm-text)" }}>{user.name || "—"}</div>
                        <div>Email</div>
                        <div data-testid="account-email" style={{ color: "var(--bm-text)" }}>{user.email}</div>
                        {user.subscription_status && canShowPlans() && (
                            <>
                                <div>Plan</div>
                                <div data-testid="account-plan" style={{ color: "var(--bm-text)" }}>
                                    {user.subscription_status === "active" ? `Pro — ${user.subscription_plan === "annual" ? "Annual" : "Monthly"}` :
                                     user.subscription_status === "lifetime" ? "Pro — Lifetime (Founder)" :
                                     user.plan_tier === "pro" ? "Pro trial" : "Free plan"}
                                </div>
                            </>
                        )}
                    </div>
                    {canShowPlans() && user.plan_tier !== "pro" && (
                        <button
                            data-testid="account-upgrade-btn"
                            className="bm-btn bm-btn-gold"
                            onClick={() => goUpgrade(nav)}
                            style={{ marginTop: 18, padding: "10px 18px", fontSize: 14 }}
                        >
                            Upgrade to Pro — from $4.99/mo
                        </button>
                    )}
                </section>

                {/* Subscription / Billing — Stripe section is web-only; Apple-billed
                    users manage through Apple (Guideline 3.1.1) */}
                {(isSubscriber || isLifetime) && (appleBilled ? canShowPlans() : billingUIEnabled()) && (
                    <section className="bm-card" data-testid="subscription-section" style={{ padding: 26, marginBottom: 24 }}>
                        <h2 className="bm-serif" style={{ fontSize: 22, margin: "0 0 6px", fontWeight: 500 }}>
                            Subscription
                        </h2>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.6, margin: "0 0 18px" }}>
                            {isLifetime
                                ? "You have lifetime access — no recurring charges, ever. Thank you for backing the project early."
                                : appleBilled
                                    ? "Your subscription is billed through your Apple ID."
                                    : "Update your payment method, change plans, or pause/cancel your subscription."}
                        </p>

                        {billingMsg && (
                            <div data-testid="subscription-banner" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.3)", color: "var(--bm-good)", fontSize: 13 }}>
                                {billingMsg}
                            </div>
                        )}
                        {billingErr && (
                            <div data-testid="subscription-err" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", color: "var(--bm-bad)", fontSize: 13 }}>
                                {billingErr}
                            </div>
                        )}

                        {isSubscriber && appleBilled && (
                            isIOSNative() ? (
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <button
                                        data-testid="apple-change-plan-btn"
                                        type="button"
                                        className="bm-btn bm-btn-ghost"
                                        onClick={() => openPaywall()}
                                    >
                                        <CreditCard size={14} /> Change plan
                                    </button>
                                    <button
                                        data-testid="apple-manage-btn"
                                        type="button"
                                        className="bm-btn bm-btn-ghost"
                                        onClick={() => openExternal(APPLE_MANAGE_SUBSCRIPTIONS_URL)}
                                    >
                                        <XCircle size={14} /> Manage or cancel
                                    </button>
                                </div>
                            ) : (
                                <p data-testid="apple-billed-note" style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: 0 }}>
                                    Manage or cancel it on your iPhone: Settings → your name → Subscriptions.
                                </p>
                            )
                        )}
                        {isSubscriber && !appleBilled && (
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button
                                    data-testid="manage-billing-btn"
                                    type="button"
                                    className="bm-btn bm-btn-ghost"
                                    onClick={openStripePortal}
                                    disabled={portalBusy}
                                >
                                    {portalBusy
                                        ? <Loader2 size={14} style={{ animation: "spin 1.2s linear infinite" }} />
                                        : <CreditCard size={14} />}
                                    {portalBusy ? " Opening…" : " Update payment method"}
                                </button>
                                <button
                                    data-testid="open-cancel-modal-btn"
                                    type="button"
                                    className="bm-btn bm-btn-ghost"
                                    onClick={() => setShowCancelModal(true)}
                                    style={{ color: "var(--bm-bad)", borderColor: "rgba(248,113,113,0.3)" }}
                                >
                                    <XCircle size={14} /> Cancel subscription
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* Connected accounts */}
                <section className="bm-card" data-testid="connected-accounts-section" style={{ padding: 26, marginBottom: 24 }}>
                    <h2 className="bm-serif" style={{ fontSize: 22, margin: "0 0 6px", fontWeight: 500 }}>
                        Connected accounts
                    </h2>
                    <p style={{ color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.6, margin: "0 0 18px" }}>
                        Sign in faster from any device by linking one-tap providers to your account.
                    </p>

                    {/* Google row */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid var(--bm-border-soft)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <GoogleG />
                            <div>
                                <div style={{ fontSize: 14, color: "var(--bm-text)" }}>Google</div>
                                <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                                    {user.has_google ? "Connected" : "Not connected"}
                                </div>
                            </div>
                        </div>
                        <div data-testid="account-google-status" style={{ fontSize: 12, color: user.has_google ? "var(--bm-good)" : "var(--bm-text-dim)" }}>
                            {user.has_google ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Check size={14} /> Linked</span> : "—"}
                        </div>
                    </div>

                    {/* Apple row */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <AppleLogo />
                            <div>
                                <div style={{ fontSize: 14, color: "var(--bm-text)" }}>Apple ID</div>
                                <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                                    {user.has_apple ? "Connected" : "Not connected"}
                                </div>
                            </div>
                        </div>
                        {user.has_apple ? (
                            <button
                                data-testid="apple-unlink-btn"
                                type="button"
                                className="bm-btn bm-btn-ghost"
                                onClick={unlinkApple}
                                disabled={linkBusy}
                                style={{ padding: "6px 12px", fontSize: 13 }}
                            >
                                {linkBusy ? <Loader2 size={14} style={{ animation: "spin 1.2s linear infinite" }} /> : <Unlink size={14} />}
                                {linkBusy ? " …" : " Unlink"}
                            </button>
                        ) : (
                            <button
                                data-testid="apple-link-btn"
                                type="button"
                                className="bm-btn bm-btn-ghost"
                                onClick={linkApple}
                                disabled={linkBusy}
                                style={{ padding: "6px 12px", fontSize: 13 }}
                            >
                                {linkBusy ? <Loader2 size={14} style={{ animation: "spin 1.2s linear infinite" }} /> : <Link2 size={14} />}
                                {linkBusy ? " Connecting…" : " Link Apple ID"}
                            </button>
                        )}
                    </div>

                    {linkErr && <div data-testid="connected-accounts-err" style={{ marginTop: 12, color: "var(--bm-bad)", fontSize: 13 }}>{linkErr}</div>}
                    {linkOk && <div data-testid="connected-accounts-ok" style={{ marginTop: 12, color: "var(--bm-good)", fontSize: 13 }}>{linkOk}</div>}
                </section>

                {/* Install the app (PWA) — renders nothing when already installed,
                    running standalone, or inside the native iOS/Android app */}
                <InstallSection />

                {/* Danger zone — delete account */}
                <section
                    className="bm-card"
                    data-testid="delete-account-section"
                    style={{ padding: 26, border: "1px solid rgba(229,87,87,0.4)", background: "rgba(229,87,87,0.04)" }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <AlertTriangle size={20} color="var(--bm-bad)" />
                        <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500, color: "var(--bm-bad)" }}>
                            Delete Account
                        </h2>
                    </div>
                    <p style={{ color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.6, margin: "0 0 6px" }}>
                        Permanently delete your Bosun's Mate account and <strong style={{ color: "var(--bm-text)" }}>all associated data</strong>.
                    </p>
                    <ul style={{ color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.7, paddingLeft: 18, margin: "0 0 16px" }}>
                        <li>Every vessel you own (and all their entries, fuel logs, photos, trips, parts)</li>
                        <li>Your co-captain access to other people's vessels</li>
                        {canShowPlans() && <li>Any active subscription will be cancelled immediately</li>}
                        <li>This action cannot be undone — no recovery possible</li>
                    </ul>

                    {!showConfirm ? (
                        <button
                            data-testid="open-delete-confirm-btn"
                            type="button"
                            className="bm-btn bm-btn-danger"
                            onClick={() => setShowConfirm(true)}
                        >
                            <Trash2 size={14} /> Delete my account
                        </button>
                    ) : (
                        <div data-testid="delete-confirm-panel" style={{ paddingTop: 6 }}>
                            <p style={{ fontSize: 13, color: "var(--bm-text)", marginBottom: 8 }}>
                                Type <code style={{ background: "var(--bm-bg-1)", padding: "2px 6px", borderRadius: 4, color: "var(--bm-gold)" }}>{user.email}</code> below to confirm:
                            </p>
                            <input
                                data-testid="delete-confirm-input"
                                className="bm-input"
                                placeholder={user.email}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                disabled={busy}
                                autoComplete="off"
                                style={{ maxWidth: 360, marginBottom: 12 }}
                            />
                            {err && <div data-testid="delete-err" style={{ color: "var(--bm-bad)", fontSize: 13, marginBottom: 10 }}>{err}</div>}
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button
                                    data-testid="confirm-delete-btn"
                                    type="button"
                                    className="bm-btn bm-btn-danger"
                                    onClick={doDelete}
                                    disabled={busy || !matches}
                                    style={{ opacity: matches ? 1 : 0.5, cursor: matches ? "pointer" : "not-allowed" }}
                                >
                                    {busy ? <Loader2 size={14} style={{ animation: "spin 1.2s linear infinite" }} /> : <Trash2 size={14} />}
                                    {busy ? "Deleting…" : "Yes, permanently delete my account"}
                                </button>
                                <button
                                    data-testid="cancel-delete-btn"
                                    type="button"
                                    className="bm-btn bm-btn-ghost"
                                    onClick={() => { setShowConfirm(false); setConfirm(""); setErr(""); }}
                                    disabled={busy}
                                >
                                    Cancel
                                </button>
                            </div>
                            <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
                        </div>
                    )}
                </section>
            </div>

            <CancellationModal
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onCanceled={(data) => { onCanceled(data); setTimeout(() => setShowCancelModal(false), 2400); }}
                onStayed={(kind) => { onStayed(kind); setTimeout(() => setShowCancelModal(false), 2400); }}
            />
        </div>
    );
}

function GoogleG() {
    return (
        <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 7.1 29.2 5 24 5c-7.5 0-13.9 4.2-17.7 9.7z" />
            <path fill="#4CAF50" d="M24 44c5.1 0 9.7-1.9 13.2-5l-6.1-5c-2 1.4-4.4 2.2-7.1 2.2-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C10 39.6 16.4 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.1 5c-.4.4 6.8-5 6.8-14.8 0-1.3-.1-2.4-.4-3.5z" />
        </svg>
    );
}

function AppleLogo() {
    return (
        <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor" style={{ color: "var(--bm-text)" }}>
            <path d="M11.182.008c.085 1.117-.302 2.04-.94 2.733-.611.673-1.529 1.211-2.456 1.13C7.69 2.85 8.16 1.917 8.74 1.244 9.36.519 10.434.057 11.182.008zM14.32 12.057a4.55 4.55 0 0 1-.706 1.282c-.46.69-1.108 1.55-1.91 1.557-.713.006-.897-.466-1.866-.456-.97.01-1.17.467-1.886.461-.802-.007-1.418-.78-1.879-1.466-1.286-1.911-2.097-5.158-.876-7.349.59-1.062 1.69-1.748 2.847-1.765.78-.014 1.519.526 1.999.526.477 0 1.376-.65 2.319-.554.395.017 1.504.16 2.216 1.207-.06.04-1.323.774-1.309 2.31.017 1.836 1.612 2.446 1.622 2.451z" />
        </svg>
    );
}

function InstallSection() {
    const { available, platform } = usePwaInstall();
    if (!available) return null;
    return (
        <section className="bm-card" data-testid="install-app-section" style={{ padding: 26, marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <Smartphone size={20} color="var(--bm-gold)" />
                <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>
                    Install the app
                </h2>
            </div>
            <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: "0 0 16px", lineHeight: 1.6 }}>
                {platform === "ios"
                    ? "Put Bosun's Mate on your home screen — full-screen, faster, and no App Store required."
                    : "Install Bosun's Mate to your home screen — full-screen, faster, and it works on the dock."}
            </p>
            <InstallButton
                label="Install Bosun's Mate"
                className="bm-btn bm-btn-gold"
                style={{ padding: "10px 18px", fontSize: 14 }}
                testid="settings-install-app-btn"
            />
        </section>
    );
}
